import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getRemoteConfig } from 'firebase/remote-config';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';

import { getFirebaseConfig, getEnvironment, getConfig } from './environment';

// Get Firebase configuration from environment
const firebaseConfig = getFirebaseConfig();
const environment = getEnvironment();
const config = getConfig();

// Initialize Firebase (or get existing instance)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Initialize optional services based on environment
export const initializeOptionalServices = async () => {
  // Analytics (only in production and if supported)
  let analytics = null;
  if (config.enableAnalytics) {
    const analyticsSupported = await isSupported();
    if (analyticsSupported) {
      analytics = getAnalytics(app);
      console.info('Firebase Analytics initialized');
    }
  }

  // Performance monitoring (only in production)
  let performance = null;
  if (config.enablePerformanceMonitoring) {
    performance = getPerformance(app);
    console.info('Firebase Performance Monitoring initialized');
  }

  // Remote config
  const remoteConfig = getRemoteConfig(app);
  
  // Messaging (if supported)
  let messaging = null;
  if (await isMessagingSupported()) {
    messaging = getMessaging(app);
    console.info('Firebase Cloud Messaging initialized');
  }

  return { analytics, performance, remoteConfig, messaging };
};

// Connect to emulators in development environment
if (environment === 'development') {
  // Check if we should use emulators (can be controlled via env var)
  const useEmulators = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';
  
  if (useEmulators) {
    // Auth emulator
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    console.info('Connected to Firebase Auth Emulator');
    
    // Firestore emulator
    connectFirestoreEmulator(db, 'localhost', 8080);
    console.info('Connected to Firestore Emulator');
    
    // Storage emulator
    connectStorageEmulator(storage, 'localhost', 9199);
    console.info('Connected to Firebase Storage Emulator');
    
    // Functions emulator
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.info('Connected to Firebase Functions Emulator');
  }
}

export default app; 